import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { scrollToSection } from "@/App";

const NAV = [
  { label: "Forside", to: "/" },
  {
    label: "Vi tilbyder",
    to: "/vi-tilbyder",
    children: [
      { label: "Implementering", to: "/vi-tilbyder/implementering" },
      { label: "Kurser og undervisning", to: "/vi-tilbyder/kurser" },
      { label: "Supervision og faglig sparring", to: "/vi-tilbyder/supervision" },
    ],
  },
  {
    label: "Viden",
    to: "/viden",
    children: [
      { label: "Hvad er traumer?", to: "/viden#hvad-er-traumer" },
      { label: "Hvad er traumereaktioner?", to: "/viden#hvad-er-traumereaktioner" },
      { label: "Hvad er traumebevidst tilgang?", to: "/viden#hvad-er-traumebevidst-tilgang" },
    ],
  },
  {
    label: "Ressourcer",
    to: "/ressourcer",
    children: [
      { label: "Forskning", to: "/ressourcer/forskning" },
      { label: "Debat & medier", to: "/ressourcer/debat-og-medier" },
      { label: "Levede erfaringer", to: "/ressourcer/levede-erfaringer" },
      { label: "Se & lyt", to: "/ressourcer/se-og-lyt" },
      { label: "Bøger & publikationer", to: "/ressourcer/boeger-og-publikationer" },
      { label: "Værktøjer & materialer", to: "/ressourcer/vaerktoejer-og-materialer" },
      { label: "Andre udvalgte ressourcer", to: "/ressourcer/andre-udvalgte-ressourcer" },
    ],
  },
  { label: "Om os", to: "/om-os" },
  { label: "Kontakt", target: "#kontakt" },
];
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-").replace(/[^a-zæøå0-9-]/g, "").replace(/-+/g, "-");

const Header = () => {
  const [open, setOpen] = useState(false);
  const go = (e, target) => {
    if (!target) return;
    e.preventDefault();
    setOpen(false);
    scrollToSection(target);
  };
  const linkCls = "text-sm text-[#2F3338]/80 transition-colors duration-300 hover:text-[#3F5A47]";
  const mobileCls = "font-serif text-3xl text-[#3F5A47]";
  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50">
      <div className="border-b border-[#D6C2A6]/25 bg-[#FAF7F1]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 lg:py-5">
        <Link to="/" data-testid="header-logo" aria-label="Traumebevidst Udvikling – forsiden">
          <img src="/brand/logo.png" alt="Traumebevidst Udvikling" className="h-10 w-auto lg:h-12" />
        </Link>
        <nav className="hidden items-center gap-9 lg:flex" aria-label="Primær navigation">
          {NAV.map((item) =>
            item.to ? (
              item.children ? (
                <div key={item.label} className="group relative">
                  <Link
                    to={item.to}
                    data-testid={`nav-link-${slug(item.label)}`}
                    className={`${linkCls} inline-flex items-center gap-1.5`}
                  >
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
                  </Link>
                  <div
                    data-testid={`nav-dropdown-${slug(item.label)}`}
                    className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
                  >
                    <div className="min-w-60 rounded-lg border border-[#D6C2A6]/50 bg-[#FAF7F1] p-2.5 shadow-[0_14px_36px_rgba(47,51,56,0.10)]">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.to}
                          data-testid={`nav-sublink-${slug(child.label)}`}
                          className="block rounded-md px-4 py-2.5 text-sm text-[#2F3338]/80 transition-colors duration-200 hover:bg-[#D6C2A6]/20 hover:text-[#3F5A47]"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link key={item.label} to={item.to} data-testid={`nav-link-${slug(item.label)}`} className={linkCls}>
                  {item.label}
                </Link>
              )
            ) : (
              <a
                key={item.label}
                href="#"
                onClick={(e) => go(e, item.target)}
                data-testid={`nav-link-${slug(item.label)}`}
                className={linkCls}
              >
                {item.label}
              </a>
            )
          )}
          <a
            href="#"
            onClick={(e) => go(e, "#kontakt")}
            data-testid="header-cta-kontakt"
            className="rounded-full bg-[#3F5A47] px-5 py-2.5 text-sm font-medium text-[#FAF7F1] transition-colors duration-300 hover:bg-[#2F3338]"
          >
            Kontakt os
          </a>
        </nav>
        <button
          data-testid="mobile-menu-button"
          className="text-[#3F5A47] lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Luk menu" : "Åbn menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 top-[72px] z-40 flex flex-col items-center gap-7 overflow-y-auto bg-[#FAF7F1] px-6 py-12 lg:hidden"
            aria-label="Mobil navigation"
          >
            {NAV.map((item) =>
              item.to ? (
                <div key={item.label} className="flex flex-col items-center gap-4">
                  <Link
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${slug(item.label)}`}
                    className={mobileCls}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="flex flex-col items-center gap-3">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.to}
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-sublink-${slug(child.label)}`}
                          className="font-serif text-lg italic text-[#5B7F7E]"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={item.label}
                  href="#"
                  onClick={(e) => go(e, item.target)}
                  data-testid={`mobile-nav-link-${slug(item.label)}`}
                  className={mobileCls}
                >
                  {item.label}
                </a>
              )
            )}
            <a
              href="#"
              onClick={(e) => go(e, "#kontakt")}
              data-testid="mobile-cta-kontakt"
              className="mt-4 rounded-full bg-[#3F5A47] px-8 py-3 text-sm font-medium text-[#FAF7F1]"
            >
              Kontakt os
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
